/**
 * Dashboard attention section — "Your submissions need you".
 *
 * Lists the logged-in user's OWN feedback tickets (regardless of role) that
 * are sitting in the submitter's court:
 *   - 'Ready for Testing'    → "ready for you to verify"
 *   - 'Waiting on Submitter' → "we asked you a question — please reply"
 *
 * One ticket per row, each linking to /my-feedback/<key>. On initial load the
 * section pulses (~3 glow cycles via CSS keyframes) to grab attention, then
 * settles into a calm static amber highlight. `prefers-reduced-motion` users
 * get the static highlight only — no animation, and NEVER infinite flashing.
 *
 * Renders nothing while loading and nothing when no tickets qualify.
 */
import { useEffect, useState } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import { ArrowForward, NotificationsActive } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { getMyTickets } from '../../services/feedbackService';
import { STATUS_COLORS } from '../feedback/feedbackShared';
import { attentionMessage, filterAttentionTickets } from './attentionTickets';
import type { FeedbackTicket } from '../../types/feedback';

export const MyTicketsAttention = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<FeedbackTicket[]>([]);

  useEffect(() => {
    let cancelled = false;
    getMyTickets()
      .then((all) => { if (!cancelled) setTickets(filterAttentionTickets(all)); })
      .catch(() => { /* dashboard extra — fail silent */ });
    return () => { cancelled = true; };
  }, []);

  // Empty state: no section at all — the dashboard stays uncluttered.
  if (tickets.length === 0) return null;

  const accent = theme.palette.warning.main;

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2, p: { xs: 1.5, md: 1.75 },
        borderRadius: '12px',
        border: '1px solid', borderColor: alpha(accent, 0.5),
        // Settled state: calm static amber highlight (also the reduced-motion state).
        backgroundColor: alpha(accent, 0.06),
        // Attention at load: ~3 pulse cycles of a glow + slightly deeper tint,
        // then the animation ends and the static style above remains.
        animation: 'myTicketsAttentionPulse 1.1s ease-in-out 0.2s 3',
        '@keyframes myTicketsAttentionPulse': {
          '0%, 100%': {
            boxShadow: `0 0 0 0 ${alpha(accent, 0)}`,
            backgroundColor: alpha(accent, 0.06),
          },
          '50%': {
            boxShadow: `0 0 0 7px ${alpha(accent, 0.28)}`,
            backgroundColor: alpha(accent, 0.16),
          },
        },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <NotificationsActive sx={{ fontSize: 20, color: accent }} />
        <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', lineHeight: 1.2 }}>
          Your submissions need you
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
          {tickets.length} ticket{tickets.length === 1 ? '' : 's'} waiting on your action
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {tickets.map((t, i) => {
          const statusColor = STATUS_COLORS[t.Status] || theme.palette.text.secondary;
          return (
            <Box
              key={t.FeedbackTicketKey}
              onClick={() => navigate(`${ROUTES.MY_FEEDBACK}/${t.FeedbackTicketKey}`)}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.25,
                px: 1.25, py: 0.9, borderRadius: 1.5, cursor: 'pointer',
                borderBottom: i < tickets.length - 1
                  ? `1px solid ${alpha(accent, 0.18)}` : 'none',
                transition: 'background-color 0.15s',
                '&:hover': { backgroundColor: alpha(accent, 0.12) },
              }}
            >
              <Typography sx={{
                fontFamily: 'monospace', fontWeight: 700, fontSize: '0.74rem',
                color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap',
              }}>
                {t.TicketNumber}
              </Typography>
              <Typography sx={{
                fontSize: '0.84rem', fontWeight: 600, color: 'text.primary',
                flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {t.RefinedTitle || t.Title}
              </Typography>
              <Chip
                label={t.Status}
                size="small"
                sx={{
                  fontSize: '0.7rem', height: 22, fontWeight: 600, flexShrink: 0,
                  bgcolor: alpha(statusColor, 0.14), color: statusColor,
                }}
              />
              <Typography sx={{
                fontSize: '0.76rem', fontStyle: 'italic', color: statusColor,
                flexShrink: 0, display: { xs: 'none', sm: 'block' },
              }}>
                {attentionMessage(t.Status)}
              </Typography>
              <ArrowForward sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};
